import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts'
import { CHART_COLORS, CHART_PALETTE } from '@/lib/chart-theme'
import { StatCard, fmt$, InfoTooltip } from './shared'

export function EstimatorComparisonReport({ data }: { data: any }) {
  const { summary, chartData } = data
  const { byEstimator } = chartData

  // Build radar data with normalized metrics (0-100 scale)
  const metrics = ['Estimates', 'Approval %', 'Close %', 'Won', 'Revenue']

  // First pass: collect raw values
  const radarDataRaw = metrics.map(metric => {
    const entry: any = { metric }
    byEstimator.forEach((est: any) => {
      let val = 0
      if (metric === 'Estimates') val = est.estimatesCreated
      else if (metric === 'Approval %') val = est.approvalRate
      else if (metric === 'Close %') val = est.closeRate
      else if (metric === 'Won') val = est.proposalsSigned
      else if (metric === 'Revenue') val = est.totalRevenue / 1000 // scale to thousands
      entry[est.estimatorName] = val
    })
    return entry
  })

  // Second pass: normalize each estimator column to 0-100 scale
  const maxValues = metrics.map((metric) =>
    Math.max(...byEstimator.map((est: any) => {
      if (metric === 'Estimates') return est.estimatesCreated
      if (metric === 'Approval %') return est.approvalRate
      if (metric === 'Close %') return est.closeRate
      if (metric === 'Won') return est.proposalsSigned
      if (metric === 'Revenue') return est.totalRevenue / 1000
      return 0
    }))
  )

  const radarData = radarDataRaw.map((entry: any) => {
    const normalized: any = { metric: entry.metric }
    byEstimator.forEach((est: any) => {
      const rawVal = entry[est.estimatorName]
      const maxVal = maxValues[metrics.indexOf(entry.metric)]
      normalized[est.estimatorName] = maxVal > 0 ? Math.round((rawVal / maxVal) * 100) : 0
    })
    return normalized
  })

  return (
    <div className="rep-report">
      <div className="rep-stats-row">
        <StatCard title="Estimators" value={summary.totalEstimators} />
        <StatCard title="Total Estimates" value={summary.totalEstimates} />
        <StatCard title="Avg Approval Rate" value={`${summary.avgApprovalRate}%`} color={CHART_COLORS.coral} />
        <StatCard title="Avg Close Rate" value={`${summary.avgCloseRate}%`} color={CHART_COLORS.blue} />
      </div>

      <div className="rep-charts-row">
        <div className="rep-chart-card rep-chart-full">
          <InfoTooltip text="A radar chart maps multiple performance dimensions on a single shape, so you can spot each estimator's strengths and weaknesses at a glance." />
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={radarData} margin={{ top: 20, right: 100, bottom: 20, left: 20 }}>
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12, fill: '#64748B' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 11, fill: '#94A3B8' }} />
              {byEstimator.map((est: any, idx: number) => (
                <Radar
                  key={est.estimatorId}
                  name={est.estimatorName}
                  dataKey={est.estimatorName}
                  stroke={CHART_PALETTE[idx % CHART_PALETTE.length]}
                  fill={CHART_PALETTE[idx % CHART_PALETTE.length]}
                  fillOpacity={0.15}
                  strokeWidth={2}
                />
              ))}
            </RadarChart>
          </ResponsiveContainer>
          <div className="rep-area-legend">
            {byEstimator.map((est: any, idx: number) => (
              <span className="rep-area-legend-item" key={est.estimatorId}>
                <span className="rep-area-legend-dot" style={{ background: CHART_PALETTE[idx % CHART_PALETTE.length] }} />
                {est.estimatorName}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="rep-table-card">
        <h3 className="rep-chart-title">Detailed Comparison</h3>
        <table className="rep-table">
          <thead>
            <tr><th>Estimator</th><th>Estimates</th><th>Approved</th><th>Approval %</th><th>Proposals Won</th><th>Close %</th><th>Revenue</th></tr>
          </thead>
          <tbody>
            {byEstimator.map((r: any) => (
              <tr key={r.estimatorId}>
                <td>{r.estimatorName}</td><td>{r.estimatesCreated}</td><td>{r.approved}</td>
                <td>{r.approvalRate}%</td><td>{r.proposalsSigned}</td><td>{r.closeRate}%</td><td>{fmt$(r.totalRevenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
